import React from 'react';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { Controller } from 'react-hook-form';
import FormToolTip from './FormToolTip';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { ErrorMessage } from '@hookform/error-message';
import { useDispatch, useSelector } from 'react-redux';
import { updatePrepopulateConfig } from '../../../features/contactInfoSlice';
import { useEffect } from 'react';
import { RootState } from '../../../store/store';
import Box from '@mui/material/Box';
import UiFormLabel from '../FormLabel/FormLabel';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { isMegaScreenerStudy } from '../../../lib/utils/siteHelper';
import {
  resolvePrePopulateCheckboxField,
  resolveReadOnlyCheckboxField,
  hasMegaScreenerPrepopulateValue,
} from '../../ContactInfo/prepopulateField';

enum GENDER {
  M = 'Male',
  F = 'Female',
}

export const FormInputRadio: React.FC<any> = (props) => {
  const {
    name,
    control,
    options,
    toolTip,
    label,
    rules,
    errors,
    isReadOnly,
    isMegaScreenerReadOnly,
    prePopulateFromScreener,
    prePopulateFromMegaScreener,
  } = props;
  const dispatch = useDispatch();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const isMegaScreenerSite = isMegaScreenerStudy(siteSettings);
  const activePrePopulateField = resolvePrePopulateCheckboxField(
    isMegaScreenerSite,
    prePopulateFromScreener,
    prePopulateFromMegaScreener
  );
  const activeReadOnlyField = resolveReadOnlyCheckboxField(
    isMegaScreenerSite,
    isReadOnly,
    isMegaScreenerReadOnly
  );
  const prepopulateConfig = useSelector(
    (state: RootState) => state.contactInfoDetails.prepopulateConfig
  );
  const isGenderValueAvailable = useSelector((state: RootState) => state.screenerOutput.g);
  const cdpData = useSelector((state: RootState) => state?.cdpData);
  const screenerOutcomeParameter = useSelector(
    (state: RootState) => state.megaScreener?.screenerOutcomeParameter
  );
  const isMegaGenderValueAvailable = hasMegaScreenerPrepopulateValue(
    name,
    screenerOutcomeParameter,
    cdpData
  );
  useEffect(() => {
    dispatch(
      updatePrepopulateConfig({
        [name]: {
          readOnly: activeReadOnlyField?.value,
          prePopulate: activePrePopulateField?.value,
        },
      })
    );
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value, onBlur } }) => {
        return (
          <FormControl>
            <UiFormLabel>
              <Text field={label} />
              {toolTip && <FormToolTip value={toolTip} />}
            </UiFormLabel>
            <RadioGroup
              className="radio-group"
              aria-label={name}
              sx={{
                color: (theme) => theme.palette.neutral8.main,
                fontSize: '0.875rem',
              }}
              defaultValue={
                isGenderValueAvailable && activePrePopulateField?.value
                  ? GENDER[isGenderValueAvailable as keyof typeof GENDER]
                  : undefined
              }
              value={value}
              row
              onChange={(e) => {
                onChange(e);
              }}
              onBlur={onBlur}
            >
              {options.map((item: any, index: any) => (
                <FormControlLabel
                  key={index}
                  name={item.key}
                  value={item.value}
                  label={<Text field={item.label} />}
                  disableTypography={true}
                  disabled={
                    prepopulateConfig[name]?.readOnly === true &&
                    prepopulateConfig[name]?.prePopulate === true &&
                    (isMegaScreenerSite
                      ? isMegaGenderValueAvailable && !!value
                      : !!isGenderValueAvailable)
                  }
                  control={
                    <Radio
                      id={item.key ?? item.value}
                      sx={{
                        '& .MuiRadio-root': {
                          padding: 0,
                        },
                      }}
                    />
                  }
                />
              ))}
            </RadioGroup>
            <ErrorMessage
              errors={errors}
              name={name}
              render={({ message }) => (
                <Box
                  className="radiogroup-error"
                  sx={{
                    color: (theme: import('@mui/material/styles').Theme) =>
                      theme.palette.error.main,
                    fontSize: '0.8125rem',
                  }}
                >
                  {message}
                </Box>
              )}
            />
          </FormControl>
        );
      }}
    />
  );
};
